import { type DotVotingItem } from '../types';
import { Circle, CheckCircle2 } from 'lucide-react';

interface VotingViewProps {
  items: DotVotingItem[];
  dotsPerParticipant: number;
  remainingDots: number;
  onVote: (itemId: string) => void;
  onRemoveVote: (itemId: string) => void;
  onFinish: () => void;
}

export function VotingView({
  items,
  dotsPerParticipant,
  remainingDots,
  onVote,
  onRemoveVote,
  onFinish,
}: VotingViewProps) {
  const totalDotsUsed = dotsPerParticipant - remainingDots;

  const getDotColor = (index: number): string => {
    const colors = [
      'bg-[var(--coral)]',
      'bg-[var(--mustard)]',
      'bg-[var(--green)]',
      'bg-[var(--slate)]',
      'bg-[var(--slate)]',
      'bg-[var(--coral)]',
      'bg-[var(--slate)]',
      'bg-[var(--mustard)]',
    ];
    return colors[index % colors.length];
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6 pb-4 border-b border-[var(--rule)]">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h2 className="text-2xl font-bold text-[var(--text-pri)] mb-1">
              Allocate Your Dots
            </h2>
            <p className="text-sm text-[var(--text-sec)]">
              Click on items to place your dots. Click again to remove a dot.
            </p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-[var(--text-pri)]">
              {remainingDots} <span className="text-sm font-normal text-[var(--text-sec)]">remaining</span>
            </div>
            <div className="text-sm text-[var(--text-sec)]">
              {totalDotsUsed} of {dotsPerParticipant} used
            </div>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-8">
        <div className="w-full bg-[var(--hover-tint)] rounded-full h-3">
          <div
            className="bg-[var(--slate)] h-3 rounded-full transition-all duration-300"
            style={{ width: `${(totalDotsUsed / dotsPerParticipant) * 100}%` }}
          />
        </div>
      </div>

      {/* Items Grid */}
      <div className="mb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((item) => {
            const voteCount = item.votes;
            const hasVotes = voteCount > 0;

            return (
              <div
                key={item.id}
                className={`relative p-6  border-2 transition-all duration-200 ${
                  hasVotes
                    ? 'border-[var(--slate)] bg-[var(--slate-dim)] shadow-md'
                    : 'border-[var(--rule)] bg-[var(--bg-raised)] hover:border-[var(--rule)] hover:shadow-sm'
                }`}
              >
                <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-4 pr-12">
                  {item.name}
                </h3>

                {/* Dots Display */}
                <div className="flex flex-wrap gap-2 mb-4 min-h-[2rem]">
                  {Array.from({ length: voteCount }).map((_, index) => (
                    <div
                      key={index}
                      className={`w-6 h-6 rounded-full ${getDotColor(index)} shadow-sm flex items-center justify-center text-white text-xs font-bold`}
                      title={`Dot ${index + 1}`}
                    >
                      {index + 1}
                    </div>
                  ))}
                </div>

                {/* Vote Count */}
                <div className="text-sm text-[var(--text-sec)] mb-3">
                  {voteCount} vote{voteCount !== 1 ? 's' : ''}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2">
                  <button
                    onClick={() => onVote(item.id)}
                    disabled={remainingDots === 0}
                    className={`flex-1 px-4 py-2  text-sm font-medium transition-colors ${
                      remainingDots > 0
                        ? 'bg-[var(--slate)] text-white hover:bg-[var(--slate)]'
                        : 'bg-[var(--hover-tint)] text-[var(--text-ter)] cursor-not-allowed'
                    }`}
                  >
                    <Circle className="h-4 w-4 inline mr-1" />
                    Add Dot
                  </button>
                  {hasVotes && (
                    <button
                      onClick={() => onRemoveVote(item.id)}
                      className="px-4 py-2 bg-[var(--coral-dim)] text-[var(--coral)] hover:bg-[var(--coral-dim)] transition-colors text-sm font-medium"
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Finish Button */}
      <div className="text-center">
        <button
          onClick={onFinish}
          disabled={remainingDots > 0}
          className={`px-8 py-3  font-medium text-lg transition-colors ${
            remainingDots === 0
              ? 'bg-[var(--green)] text-white hover:bg-[var(--green)]'
              : 'bg-[var(--rule-bright)] text-[var(--text-ter)] cursor-not-allowed'
          }`}
        >
          {remainingDots === 0 ? (
            <>
              <CheckCircle2 className="h-5 w-5 inline mr-2" />
              Finish Voting
            </>
          ) : (
            `Allocate ${remainingDots} more dot${remainingDots !== 1 ? 's' : ''} to finish`
          )}
        </button>
      </div>
    </div>
  );
}


